if (!customElements.get('aura-room-guide')) {
  customElements.define('aura-room-guide', class extends HTMLElement {
    connectedCallback() {
      if (this.sceneObserver || this.initialized) return;
      const configNode = this.querySelector('[data-room-config]');
      if (!configNode) return;
      this.config = JSON.parse(configNode.textContent);
      this.sceneObserver = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) this.ensureInitialized();
      }, { rootMargin: '200px 0px' });
      this.sceneObserver.observe(this.querySelector('.rg-layout'));
      this.wake = () => this.ensureInitialized();
      this.querySelector('.rg-layout').addEventListener('focusin', this.wake, { once: true });
      this.querySelector('.rg-layout').addEventListener('pointerdown', this.wake, { once: true });
    }
    ensureInitialized() {
      this.sceneObserver?.disconnect();
      this.sceneObserver = null;
      if (this.initialized) return;
      const configNode = this.querySelector('[data-room-config]');
      if (!configNode) return;
      this.initialized = true;
      if (this.dataset.roomUrl) this.style.setProperty('--aura-room-url', `url("${this.dataset.roomUrl}")`);
      if (this.dataset.furnitureUrl) this.style.setProperty('--aura-furniture-url', `url("${this.dataset.furnitureUrl}")`);
      this.config = JSON.parse(configNode.textContent);
      this.q = selector => this.querySelector(selector);
      this.qa = selector => [...this.querySelectorAll(selector)];
      this.labels = { ...this.config.labels };
      const wall = this.querySelector('aura-wall-preview[data-ready-made="true"]');
      this.autoAddToCart = !!wall;
      if (wall?.dataset.sizeCartLabel) this.labels.keep = wall.dataset.sizeCartLabel;
      this.sizeIndex = this.config.options.findIndex(option => /size/i.test(option.name));
      const values = this.config.options[this.sizeIndex]?.values || [];
      this.sizes = values.map(value => typeof value === 'string' ? value : value.name).filter(value => this.parseSize(value));
      if (!this.sizes.length || !this.config.art.image) { this.hidden = true; return; }
      this.names = { sofa: this.labels.sofa, bed: this.labels.bed, console: this.labels.console };
      this.defaults = { sofa: 84, bed: 64, console: 60 };
      this.state = { furniture: 'sofa', width: 84, size: this.config.selectedOptions[this.sizeIndex] || this.sizes[0], unit: 'in' };
      try {
        const previous = JSON.parse(localStorage.getItem('aura-room-guide:' + this.config.productId) || 'null');
        if (previous) Object.assign(this.state, previous);
      } catch {}
      this.art = this.q('.rg-artwork-img');
      this.print = document.createElement('div');
      Object.assign(this.print.style, { position: 'absolute', overflow: 'hidden' });
      this.art.before(this.print);
      this.print.append(this.art);
      this.art.onload = () => this.paint();
      this.art.src = this.config.art.image;
      this.select = this.q('[data-role=size]');
      this.select.replaceChildren(...this.sizes.map(size => {
        const option = document.createElement('option');
        option.value = size;
        const [w, h] = this.parseSize(size);
        option.textContent = `${w} × ${h} ${this.labels.inches}`;
        return option;
      }));
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button) return;
        if (button.dataset.furniture) {
          this.state.furniture = button.dataset.furniture;
          this.state.width = this.defaults[this.state.furniture];
          this.saved = false;
        } else if (button.dataset.unit) this.state.unit = button.dataset.unit;
        else if (button.dataset.role === 'keep') { this.applyPurchaseSize(); return; }
        else return;
        this.render(); this.remember();
      }, { signal });
      this.select.addEventListener('change', () => { this.state.size = this.select.value; this.saved = false; this.render(); this.remember(); }, { signal });
      this.q('[data-role=width]').addEventListener('input', event => {
        const inches = this.fromInput(event.target.value);
        if (inches < 24 || inches > 144 || !Number.isFinite(inches)) return;
        this.state.width = inches; this.saved = false;
        this.q('[data-role=range]').value = this.display(inches);
        this.updateSelection(); this.paint();
      }, { signal });
      ['change', 'blur'].forEach(name => this.q('[data-role=width]').addEventListener(name, event => {
        this.updateWidth(event.target.value); this.remember();
      }, { signal }));
      this.q('[data-role=range]').addEventListener('input', event => this.updateWidth(event.target.value), { signal });
      this.q('[data-role=range]').addEventListener('change', () => this.remember(), { signal });
      document.addEventListener('variant:update', event => {
        if (this.applying) return;
        if (String(event.detail?.data?.productId) !== String(this.config.productId)) return;
        const size = event.detail.resource?.options?.[this.sizeIndex];
        if (this.sizes.includes(size)) { this.state.size = size; this.saved = false; this.render(); }
      }, { signal });
      this.variantData = JSON.parse(this.querySelector('[data-room-variants]')?.textContent || '{"variants":[]}');
      this.scheduleSync = () => {
        clearTimeout(this.syncTimer);
        this.syncTimer = setTimeout(() => { if (!this.applying) this.syncPurchaseOptions(); }, 100);
      };
      document.addEventListener('change', event => {
        if (event.target.closest('.product-information')) this.scheduleSync();
      }, { signal });
      document.addEventListener('variant:update', this.scheduleSync, { signal });
      this.purchaseObserver = new MutationObserver(this.scheduleSync);
      const purchaseScope = this.purchaseParts().scope;
      if (purchaseScope) this.purchaseObserver.observe(purchaseScope, { childList: true, subtree: true, attributes: true, attributeFilter: ['checked', 'disabled', 'data-variantid'] });
      this.syncPurchaseOptions();
      this.observer = new ResizeObserver(entries => {
        const { width, height } = entries[0].contentRect;
        this.sceneSize = { width, height };
        this.paint();
      });
      this.observer.observe(this.q('.rg-scene'));
      this.render();
    }
    disconnectedCallback() { this.sceneObserver?.disconnect(); this.sceneObserver = null; this.querySelector('.rg-layout')?.removeEventListener('focusin', this.wake); this.querySelector('.rg-layout')?.removeEventListener('pointerdown', this.wake); this.abort?.abort(); this.observer?.disconnect(); this.purchaseObserver?.disconnect(); clearTimeout(this.syncTimer); cancelAnimationFrame(this.paintFrame); this.paintFrame = null; this.lastPurchaseSignature = null; this.initialized = false; }
    setCustomArtwork(artwork) {
      this.ensureInitialized();
      if (!this.initialized) return;
      this.originalArt ||= {...this.config.art};
      this.customArtwork = artwork;
      if (!artwork) { this.config.art = {...this.originalArt}; this.art.src=this.config.art.image; }
      this.refreshCustomArtwork();
      this.paint();
    }
    refreshCustomArtwork(size = this.state?.size) {
      if (!this.customArtwork || !size) return;
      const image = this.customArtwork.render(size);
      if (!image || this.config.art.image === image) return;
      this.config.art = {image, crop:[0,0,1,1]};
      this.art.src=image;
    }
    syncPurchaseOptions() {
      const selected = this.config.options.map((option, index) => document.querySelector(`.product-information .swatch--gl[data-option-index="${index}"] input:checked`)?.value || this.config.selectedOptions[index]);
      const styleIndex = this.config.options.findIndex(option => /frame style|format|material/i.test(option.name));
      const colorIndex = this.config.options.findIndex(option => /color|colour|finish/i.test(option.name));
      this.format = /poster/i.test(selected[styleIndex]) ? 'poster' : /stretch/i.test(selected[styleIndex]) ? 'stretched' : 'framed';
      this.finish = selected[colorIndex] || '';
      const variants = this.variantData.variants;
      let valid;
      if (variants.length && variants.length < 250) {
        valid = variants.filter(variant => variant.available && variant.options.every((value, index) => index === this.sizeIndex || value === selected[index])).map(variant => variant.options[this.sizeIndex]);
      } else {
        const group = this.purchaseParts().group;
        valid = [...group?.querySelectorAll('input[type=radio]') || []].filter(input => !input.disabled && input.closest('li')?.dataset.variantid && !input.closest('li').matches('.globo-out-of-stock,.disabled,[aria-disabled="true"]')).map(input => input.value);
        if (!group) valid = this.sizes;
      }
      const availableSizes = [...new Set(valid)].filter(size => this.sizes.includes(size));
      const signature = JSON.stringify([selected, availableSizes]);
      if (signature === this.lastPurchaseSignature) return;
      this.lastPurchaseSignature = signature;
      const sizeListChanged = JSON.stringify(availableSizes) !== JSON.stringify(this.availableSizes);
      this.availableSizes = availableSizes;
      if (sizeListChanged) this.select.replaceChildren(...this.availableSizes.map(size => {
        const option = document.createElement('option');
        option.value = size;
        option.textContent = this.parseSize(size).join(' × ') + ' ' + this.labels.inches;
        return option;
      }));
      const selectionChanged = this.lastPurchaseOptions !== JSON.stringify(selected);
      if (selectionChanged || !this.availableSizes.includes(this.state.size)) this.state.size = this.availableSizes.includes(selected[this.sizeIndex]) ? selected[this.sizeIndex] : this.availableSizes[0] || this.sizes[0];
      this.lastPurchaseOptions = JSON.stringify(selected);
      this.select.disabled = !this.availableSizes.length;
      this.q('[data-role=keep]').disabled = !this.availableSizes.length;
      this.q('.rg-frame').hidden = !this.availableSizes.length;
      this.dataset.format = this.format;
      if (selectionChanged) this.saved = false;
      this.render();
    }
    parseSize(value) {
      const match = String(value).match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i);
      return match && Number(match[1]) > 0 && Number(match[2]) > 0 ? [Number(match[1]), Number(match[2])] : null;
    }
    display(inches) { return Number((this.state.unit === 'cm' ? inches * 2.54 : inches).toFixed(1)).toString(); }
    fromInput(value) { return Number(value) / (this.state.unit === 'cm' ? 2.54 : 1); }
    updateWidth(value) {
      const width = this.fromInput(value);
      if (!Number.isFinite(width) || width <= 0) { this.render(); return; }
      this.state.width = Math.max(24, Math.min(144, width)); this.saved = false; this.render();
    }
    remember() { try { localStorage.setItem('aura-room-guide:' + this.config.productId, JSON.stringify(this.state)); } catch {} }
    purchaseParts() {
      const scope = document.querySelector('.product-information');
      const group = scope?.querySelector(`.swatch--gl[data-option-index="${this.sizeIndex}"]`);
      const form = scope?.querySelector('form[action*="/cart/add"]');
      return { scope, group, form };
    }
    showApplyError(message) {
      this.saved = false;
      this.q('.rg-saved').hidden = false;
      this.q('.rg-saved').textContent = message;
      this.q('[data-role=keep]>span').textContent = this.labels.keep;
    }
    async applyPurchaseSize() {
      if (this.applying || (this.lastCartClick && Date.now() - this.lastCartClick < 2500)) return;
      const selectedSize = this.state.size;
      const { scope, group, form } = this.purchaseParts();
      if (!scope || !group || !form) { this.showApplyError(this.labels.loading); return; }
      const option = [...group.querySelectorAll('input[type="radio"]')].find(input => input.value === selectedSize);
      const item = option?.closest('li');
      const selectedOptions = this.config.options.map((option, index) => index === this.sizeIndex ? selectedSize : document.querySelector(`.product-information .swatch--gl[data-option-index="${index}"] input:checked`)?.value || this.config.selectedOptions[index]);
      const variantId = this.variantData?.variants.find(variant => variant.available && variant.options.every((value, index) => value === selectedOptions[index]))?.id || item?.dataset.variantid;
      if (!option || option.disabled || item?.matches('.globo-out-of-stock,.disabled,[aria-disabled="true"]') || !variantId) {
        this.showApplyError(this.labels.unavailable); return;
      }
      const keep = this.q('[data-role=keep]');
      this.applying = true;
      this.saved = false;
      keep.disabled = true;
      this.select.disabled = true;
      this.q('.rg-saved').hidden = true;
      this.q('[data-role=keep]>span').textContent = this.labels.applying;
      const confirmed = () => {
        const current = this.purchaseParts();
        const checked = current.group?.querySelector('input:checked');
        const input = current.form?.querySelector('input[name="id"]');
        return checked?.value === selectedSize && input?.value === variantId && !input.disabled;
      };
      try {
        if (!confirmed()) option.click();
        const started = Date.now();
        const applied = await new Promise(resolve => {
          const check = () => {
            if (!this.isConnected) { resolve(false); return; }
            if (confirmed()) { resolve(true); return; }
            if (Date.now() - started > 6000) { resolve(false); return; }
            setTimeout(check, 80);
          };
          check();
        });
        if (!this.isConnected) return;
        if (!applied) { this.showApplyError(this.labels.apply_failed); return; }
        this.state.size = selectedSize;
        this.lastPurchaseOptions = JSON.stringify(selectedOptions);
        this.saved = true;
        this.updateSelection();
        this.remember();
        const target = this.customArtwork ? document.querySelector('#aura-easify-preview .aep-approve') : this.purchaseParts().form?.querySelector('button[type="submit"][name="add"]');
        if (target) {
          const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
          target.scrollIntoView({ behavior: reduced ? 'instant' : 'smooth', block: 'center' });
          if (!reduced) await new Promise(resolve => {
            const started = performance.now(); let last = window.scrollY; let stable = 0;
            const settle = () => {
              const current = window.scrollY;
              stable = Math.abs(current - last) < 1 ? stable + 1 : 0; last = current;
              if ((performance.now() - started > 200 && stable >= 4) || performance.now() - started > 1800) resolve();
              else requestAnimationFrame(settle);
            }; requestAnimationFrame(settle);
          });
          if (this.customArtwork) { target.focus({ preventScroll: true }); if (this.autoAddToCart && confirmed() && !target.disabled) { this.lastCartClick = Date.now(); target.click(); } return; }
          const current = this.purchaseParts().form;
          const submit = current?.querySelector('button[type="submit"][name="add"]');
          if (this.autoAddToCart && confirmed() && submit && !submit.disabled && current.reportValidity()) {
            this.lastCartClick = Date.now();
            submit.focus({ preventScroll: true });
            const component = current.closest('product-form-component');
            const added = await new Promise(resolve => {
              const controller = new AbortController();
              const finish = ok => { clearTimeout(timer); controller.abort(); resolve(ok); };
              const timer = setTimeout(() => finish(false), 15000);
              component.addEventListener('cart:update', event => finish(!event.detail?.data?.didError), { signal: controller.signal });
              component.addEventListener('cart:error', () => finish(false), { signal: controller.signal });
              submit.click();
            });
            if (added && this.isConnected) {
              await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
              const drawer = document.querySelector('cart-drawer-component');
              if (!drawer?.querySelector('dialog[open]')) drawer?.querySelector('button[aria-haspopup="dialog"]')?.click();
            }
          } else if (submit) submit.focus({ preventScroll: true });
        }
      } catch {
        if (this.isConnected) this.showApplyError(this.labels.apply_failed);
      } finally {
        this.applying = false;
        keep.disabled = false;
        this.select.disabled = false;
      }
    }
    cartSizeLabel(size = this.state.size) {
      const options = this.config.options.map((option, index) => index === this.sizeIndex ? size : document.querySelector(`.product-information .swatch--gl[data-option-index="${index}"] input:checked`)?.value || this.config.selectedOptions[index]);
      const variant = this.variantData?.variants.find(variant => variant.available && variant.options.every((value, index) => value === options[index]));
      return variant?.priceLabel ? `${this.labels.keep} - ${variant.priceLabel}` : this.labels.keep;
    }
    updateSelection() {
      const [w, h] = this.parseSize(this.state.size);
      this.q('[data-size-metric]').textContent = `${(w * 2.54).toFixed(1)} × ${(h * 2.54).toFixed(1)} cm`;
      this.q('[data-selection-size]').textContent = `${w} × ${h} ${this.labels.inches}`;
      this.q('[data-ratio]').textContent = this.labels.ratio.replace('[percent]', Math.round(w / this.state.width * 100)).replace('[furniture]', this.names[this.state.furniture].toLowerCase());
      this.q('.rg-saved').hidden = this.autoAddToCart || !this.saved;
      this.q('.rg-saved').textContent = `${w} × ${h} ${this.labels.inches} · ${this.labels.saved_note}`;
      this.q('[data-role=keep]>span').textContent = this.autoAddToCart ? this.cartSizeLabel() : this.saved ? this.labels.saved : this.labels.keep;
    }
    render() {
      if (!this.names[this.state.furniture]) this.state.furniture = 'sofa';
      if (!this.sizes.includes(this.state.size)) this.state.size = this.sizes[0];
      if (!['cm', 'in'].includes(this.state.unit)) this.state.unit = 'in';
      this.state.width = Math.max(24, Math.min(144, Number(this.state.width) || 84));
      this.qa('[data-furniture]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.furniture === this.state.furniture)));
      Object.keys(this.names).forEach(name => { this.q('.rg-' + name).hidden = name !== this.state.furniture; });
      this.qa('[data-unit]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.unit === this.state.unit)));
      this.q('[data-scene-type]').textContent = this.names[this.state.furniture].toUpperCase();
      this.q('[data-width-title]').textContent = `${this.names[this.state.furniture]} ${this.labels.width}`;
      const min = this.state.unit === 'cm' ? 61 : 24;
      const max = this.state.unit === 'cm' ? 365.8 : 144;
      ['width', 'range'].forEach(role => {
        const input = this.q('[data-role=' + role + ']');
        input.min = min; input.max = max; input.step = this.state.unit === 'cm' ? 0.1 : 1; input.value = this.display(this.state.width);
      });
      this.q('[data-min-width]').textContent = min + ' ' + this.state.unit;
      this.q('[data-max-width]').textContent = max + ' ' + this.state.unit;
      this.select.value = this.state.size;
      this.updateSelection(); this.paint();
    }
    paint() {
      if (this.paintFrame != null) return;
      this.paintFrame = requestAnimationFrame(() => {
        this.paintFrame = null;
        if (this.isConnected) this.paintScene();
      });
    }
    paintScene() {
      if (!this.state || !this.sizes) return;
      this.refreshCustomArtwork();
      const scene = this.q('.rg-scene');
      const W = this.sceneSize?.width || scene.clientWidth, H = this.sceneSize?.height || scene.clientHeight;
      if (!W) return;
      const [aw, ah] = this.parseSize(this.state.size);
      const ratios = { sofa: 378 / 1170, bed: 520 / 1050, console: 280 / 1170 };
      const ratio = ratios[this.state.furniture];
      const px = Math.min((W - 38) / Math.max(104, this.state.width * 1.15, aw * 1.45), (H - 125) / (ah + 8 + this.state.width * ratio));
      const fw = this.state.width * px, fh = fw * ratio, furnitureTop = H - 50 - fh;
      Object.assign(this.q('.rg-furniture').style, { width: fw + 'px', height: fh + 'px', left: (W - fw) / 2 + 'px', top: furnitureTop + 'px' });
      const framed = this.format !== 'poster' && this.format !== 'stretched';
      const artW = aw * px, artH = ah * px, edge = framed ? Math.max(2, px * .472) : 0, gap = framed ? Math.max(1, px * .315) : 0;
      const artTop = furnitureTop - 8 * px - artH - 2 * (edge + gap);
      const finishColor = /espresso/i.test(this.finish) ? '#382b25' : /black/i.test(this.finish) ? '#181818' : /white/i.test(this.finish) ? '#f6f5f0' : '#b48c5e';
      Object.assign(this.q('.rg-frame').style, { width: artW + 2 * gap + 'px', height: artH + 2 * gap + 'px', borderWidth: edge + 'px', borderColor: finishColor, background: '#181818', boxShadow: this.format === 'poster' ? '0 1px 2px #0002' : '2px 5px 7px #0003', left: (W - artW) / 2 - edge - gap + 'px', top: artTop + 'px' });
      const [cx, cy, cw, ch] = this.config.art.crop;
      const aspect = this.art.naturalWidth / this.art.naturalHeight || 1;
      const zoom = Math.max(artW / (cw * aspect), artH / ch);
      Object.assign(this.print.style, { width: artW + 'px', height: artH + 'px', left: gap + 'px', top: gap + 'px' });
      Object.assign(this.art.style, { width: zoom * aspect + 'px', height: zoom + 'px', left: (artW - cw * zoom * aspect) / 2 - cx * zoom * aspect + 'px', top: (artH - ch * zoom) / 2 - cy * zoom + 'px' });
      Object.assign(this.q('.rg-art-dimension').style, { width: artW + 'px', left: (W - artW) / 2 + 'px', top: artTop - 15 + 'px' });
      Object.assign(this.q('.rg-furniture-dimension').style, { width: fw + 'px', left: (W - fw) / 2 + 'px', top: H - 27 + 'px' });
      const unit = this.state.unit === 'cm' ? ' cm' : '″';
      this.q('[data-art-dimension]').textContent = this.display(aw) + unit;
      this.q('[data-furniture-dimension]').textContent = this.display(this.state.width) + unit + ' ' + this.names[this.state.furniture].toLowerCase();
      scene.setAttribute('aria-label', `${this.config.title}, ${aw} × ${ah} ${this.labels.inches}; ${this.names[this.state.furniture]} ${this.display(this.state.width)} ${this.state.unit}`);
    }
  });
}






